/**
 * React Query Hooks for Crypto.com Exchange API
 *
 * These hooks provide:
 * - Automatic refetching
 * - Caching
 * - Loading/error states
 * - Retry logic
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { cryptoComAPI, TradingBot, StakingPosition, CROCard } from '../lib/api/cryptocom-api';

/**
 * Hook to fetch trading bots from Crypto.com
 *
 * Auto-refreshes every 30 seconds when tab is active
 */
export function useTradingBots(options?: UseQueryOptions<TradingBot[], Error>) {
  return useQuery<TradingBot[], Error>({
    queryKey: ['cryptocom', 'trading-bots'],
    queryFn: () => cryptoComAPI.fetchTradingBots(),
    staleTime: 30 * 1000, // Consider data stale after 30s
    refetchInterval: 30 * 1000, // Auto-refresh every 30s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch staking positions from Crypto.com
 *
 * Auto-refreshes every 60 seconds (staking data changes less frequently)
 */
export function useStakingPositions(options?: UseQueryOptions<StakingPosition[], Error>) {
  return useQuery<StakingPosition[], Error>({
    queryKey: ['cryptocom', 'staking-positions'],
    queryFn: () => cryptoComAPI.fetchStakingPositions(),
    staleTime: 60 * 1000, // Consider data stale after 60s
    refetchInterval: 60 * 1000, // Auto-refresh every 60s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Hook to fetch CRO Card information
 *
 * Auto-refreshes every 60 seconds
 */
export function useCROCard(options?: UseQueryOptions<CROCard, Error>) {
  return useQuery<CROCard, Error>({
    queryKey: ['cryptocom', 'cro-card'],
    queryFn: () => cryptoComAPI.fetchCROCard(),
    staleTime: 60 * 1000, // Consider data stale after 60s
    refetchInterval: 60 * 1000, // Auto-refresh every 60s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
}

/**
 * Helper hook to get combined stats from all data sources
 */
export function useCryptoComStats() {
  const { data: tradingBots = [], isLoading: botsLoading } = useTradingBots();
  const { data: stakingPositions = [], isLoading: stakingLoading } = useStakingPositions();

  const totalTradingProfit = tradingBots.reduce((sum, bot) => sum + bot.profit, 0);
  const totalStakingRewards = stakingPositions.reduce((sum, pos) => sum + pos.totalRewards, 0);
  const totalStakedValue = stakingPositions.reduce(
    (sum, pos) => sum + pos.amount * (pos.instrument === 'CRO' ? 0.15 : 1),
    0
  );
  const activeBots = tradingBots.filter((bot) => bot.status === 'ACTIVE').length;

  return {
    totalTradingProfit,
    totalStakingRewards,
    totalStakedValue,
    activeBots,
    isLoading: botsLoading || stakingLoading,
  };
}
